// src/hooks/useTokenRefresh.js
// Automatisk fornyelse av access token (via refresh-cookie) mens brukeren er aktiv i appen.
import { useEffect, useRef } from 'react';
import { TIMEOUTS, authActivityVerboseLogging } from '../config/timeouts';
import { useAuth } from '../contexts/AuthContext';

/**
 * Bakgrunn:
 * - Backend setter accessToken-cookie med kort levetid (typisk 15 min).
 * - Uten periodisk refresh vil token utløpe selv om brukeren fortsatt «jobber» i skjemaet.
 * - Denne hooken kaller AuthContext.refreshToken() (POST /api/auth/refresh) med jevne mellomrom,
 *   men bare når vi anser brukeren som nylig aktiv – slik unngår vi unødvendige refresh-kall
 *   når fanen er åpen men ingen bruker er til stede.
 *
 * Når brukeren kommer tilbake til fanen (f.eks. etter lunsj), er det ofte ingen muse-/tastaturhendelse
 * før de fortsetter – da er access token ofte utløpt. `visibilitychange` → synlig utløser derfor
 * et refresh-kall (throttlet) og oppdaterer aktivitetstidspunktet.
 *
 * Viktig begrensning:
 * - Aktivitet måles ellers via hendelser på window (klikk, tastatur, scroll, touch).
 *   Bruker som leser lenge uten å røre mus/tastatur teller som «inaktiv» i denne logikken.
 * - Dette erstatter ikke andre mekanismer (f.eks. refresh ved 401 i axiosConfig).
 *
 * Logging: `config/timeouts.js` (`AUTH_ACTIVITY_CLIENT_VERBOSE`, scroll-throttle).
 */
const log = (...args) => {
	if (process.env.NODE_ENV === 'development') {
		console.log('[useTokenRefresh]', ...args);
	}
};

let lastScrollActivityLogMs = 0;

const logUserActivity = (eventType) => {
	if (!authActivityVerboseLogging) {
		return;
	}
		if (eventType === 'scroll') {
		const n = Date.now();
		if (n - lastScrollActivityLogMs < TIMEOUTS.authActivityScrollLogThrottleMs) {
			return;
		}
		lastScrollActivityLogMs = n;
	}
	console.log('[auth/activity]', new Date().toISOString(), eventType);
};

export const useTokenRefresh = () => {
	const { isLoggedIn, refreshToken } = useAuth();

	/** Tidspunkt for siste registrerte brukeraktivitet (klikk, tast, scroll, …). */
	const lastActivityRef = useRef(Date.now());

	/** Referanse til setInterval slik at vi kan rydde opp ved logout/unmount. */
	const refreshIntervalRef = useRef(null);

	/** Siste gang vi refreshet pga. at fanen ble synlig igjen (throttling). */
	const lastVisibilityRefreshRef = useRef(0);

	useEffect(() => {
		if (!isLoggedIn) {
			if (refreshIntervalRef.current) {
				log('stopper intervall (ikke innlogget)');
				clearInterval(refreshIntervalRef.current);
			}
			return;
		}

		if (TIMEOUTS.tokenRefreshPeriodicDisabled) {
			log(
				'tokenRefreshPeriodicDisabled: hopper over periodisk intervall-refresh. Kun visibility + API/401 fornyer token.'
			);
		} else {
			log(
				'startet — intervall hvert',
				TIMEOUTS.tokenRefreshIntervalMs / 60_000,
				'min, visibility-refresh ved synlig fane'
			);
		}

		const updateActivity = (e) => {
			lastActivityRef.current = Date.now();
			logUserActivity(e?.type || 'activity');
		};

		const onVisibilityChange = async () => {
			const state = document.visibilityState;
			if (state === 'hidden') {
				log('fane skjult (visibility: hidden)');
				return;
			}
			const now = Date.now();
			const sinceLastVisibilityRefresh = now - lastVisibilityRefreshRef.current;
			if (sinceLastVisibilityRefresh < TIMEOUTS.visibilityRefreshThrottleMs) {
				log(
					'fane synlig igjen — hopper over refresh (throttle)',
					`${Math.round((TIMEOUTS.visibilityRefreshThrottleMs - sinceLastVisibilityRefresh) / 1000)}s til neste tillatte`
				);
				return;
			}
			log('fane synlig igjen — forsøker token-refresh (visibility)');
			lastVisibilityRefreshRef.current = now;
			lastActivityRef.current = now;
			const ok = await refreshToken();
			log('visibility refresh-token ferdig:', ok ? 'OK' : 'feilet (se AuthContext)');
		};

		document.addEventListener('visibilitychange', onVisibilityChange);

		const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
		events.forEach((event) => {
			window.addEventListener(event, updateActivity);
		});

		/**
		 * Intervall-logikk (10 min sjekk, 15 min terskel):
		 * - Hvert 10. minutt: sjekk om det er mindre enn 15 minutter siden siste aktivitet.
		 * - Hvis ja: kall refreshToken() slik at accessToken fornyes før det utløper (15 min).
		 * - Hvis nei: ikke refresh (brukeren har sannsynligvis gått fra PC / ikke brukt appen).
		 *
		 * Hvorfor 10 min intervall når token lever 15 min?
		 * - Gir flere sjanser til å fornye innen vinduet, selv om ett intervall treffer «stille» periode.
		 * - «Kom tilbake til fanen» dekkes av visibilitychange (se over), ikke bare av dette intervallet.
		 */
		const startRefreshInterval = () => {
			refreshIntervalRef.current = setInterval(
				async () => {
					const timeSinceActivity = Date.now() - lastActivityRef.current;
					const activityThresholdMs =
						TIMEOUTS.tokenRefreshActivityThresholdMs;
					const inactiveMin = Math.round(timeSinceActivity / 60000);

					if (timeSinceActivity < activityThresholdMs) {
						log(
							'intervall:',
							TIMEOUTS.tokenRefreshIntervalMs / 60_000,
							'min — bruker aktiv siste',
							inactiveMin,
							'min — refresher token'
						);
						const ok = await refreshToken();
						log('intervall refresh-token ferdig:', ok ? 'OK' : 'feilet (se AuthContext)');
					} else {
						log(
							'intervall: hopper over — inaktiv',
							inactiveMin,
							'min siden siste aktivitet (terskel',
							activityThresholdMs / 60_000,
							'min)'
						);
					}
				},
				TIMEOUTS.tokenRefreshIntervalMs
			);
		};

		if (!TIMEOUTS.tokenRefreshPeriodicDisabled) {
			startRefreshInterval();
		}

		return () => {
			log('rydder lyttere og intervall (unmount / logout)');
			document.removeEventListener('visibilitychange', onVisibilityChange);
			events.forEach((event) => {
				window.removeEventListener(event, updateActivity);
			});
			if (refreshIntervalRef.current) {
				clearInterval(refreshIntervalRef.current);
			}
		};
	}, [isLoggedIn, refreshToken]);
};

export default useTokenRefresh;
